/**
 * Discount Synchronization Module for Shopify Minimog Theme
 *
 * Handles dynamic discount application using Discounty app with cart property
 * management. Reads from item.total_discount and syncs with cart UI.
 *
 * Features:
 * - Reads item.total_discount as single source of truth
 * - Manages _discount and _volume_discount properties
 * - Detects AJAX cart updates
 * - Polls for stable Discounty async updates
 * - Prevents infinite loops and stale data
 *
 * =============================================================================
 * FIX - quantities shrinking when the customer clicks +
 *
 * updateCartItem() used to send:
 *
 *     formData.append('updates[' + itemKey + ']', quantity);
 *
 * That is a QUANTITY WRITE, and the quantity came from a cart snapshot taken
 * inside pollForStableCart() - which can be up to pollTimeout (3s) old, on top
 * of debounceDelay (500ms).
 *
 * So: the customer clicks + (4 -> 5), polling starts and reads 5, the customer
 * clicks + again (5 -> 6), polling finishes and writes back the 5 it captured
 * earlier. Shopify obediently sets the line to 5 and the customer watches
 * their quantity go backwards. Clicking quickly could drag it back further.
 *
 * The write was never needed. This function only sets CART ATTRIBUTES, and
 * /cart/update.js accepts attributes on their own. The updates[] line has been
 * removed, so this module can no longer alter any quantity.
 *
 * Also fixed: attributes were written inside the per-item loop. Cart
 * attributes are cart-level, not line-level, so each item overwrote the last
 * and _discount ended up holding only the final item's amount rather than the
 * order total (240.00 recorded against a real 480.00). Attributes are now
 * computed from the whole cart and written once.
 * =============================================================================
 */

class DiscountSync {
  constructor(options = {}) {
    this.config = {
      pollInterval: options.pollInterval || 200, // ms between stability checks
      pollTimeout: options.pollTimeout || 3000, // max time to wait for stability
      stabilityThreshold: options.stabilityThreshold || 2, // consecutive stable reads
      debounceDelay: options.debounceDelay || 500, // debounce cart update handler
      debug: options.debug || false,
    };

    this.state = {
      isProcessing: false,
      lastKnownDiscounts: {}, // Track previous discount state
      lastWrittenAttributes: null,
      stabilityCounter: 0,
      lastCartTotal: null,
      updateQueue: [],
    };

    this.fetchCache = {
      lastFetch: null,
      lastFetchData: null,
      cacheDuration: 100, // ms
    };

    this.debounceTimer = null;

    this.initialize();
  }

  /**
   * Initialize the discount sync system
   */
  initialize() {
    this.log('Initializing DiscountSync...');

    // Intercept fetch calls to detect AJAX cart changes
    this.interceptFetch();

    // Also monitor mutation in DOM if using MutationObserver
    if (document.body) {
      this.monitorCartDrawerUpdates();
    }

    // Initial run
    setTimeout(() => this.syncDiscounts(), 500);

    this.log('DiscountSync initialized');
  }

  /**
   * Intercept fetch to detect cart API calls
   */
  interceptFetch() {
    const originalFetch = window.fetch;

    window.fetch = (...args) => {
      const [resource] = args;
      const isCartChange =
        typeof resource === 'string' &&
        (resource.includes('/cart/change') ||
          resource.includes('/cart/add') ||
          resource.includes('/cart/update'));

      if (isCartChange) {
        this.log('🔄 Cart change detected via fetch:', resource);
      }

      // Call original fetch and trigger sync after completion
      return originalFetch.apply(window, args).then((response) => {
        if (isCartChange) {
          // Debounce to avoid multiple rapid updates
          this.debouncedSync();
        }
        return response;
      });
    };
  }

  /**
   * Debounced sync to prevent rapid re-runs
   */
  debouncedSync() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.syncDiscounts();
    }, this.config.debounceDelay);
  }

  /**
   * Monitor cart drawer for DOM mutations
   */
  monitorCartDrawerUpdates() {
    const cartDrawer = document.querySelector('[data-minimog-cart-items]');
    if (!cartDrawer) {
      this.log('⚠️  Cart drawer not found, skipping MutationObserver');
      return;
    }

    const observer = new MutationObserver(() => {
      this.log('🔄 Cart drawer DOM updated');
      this.debouncedSync();
    });

    observer.observe(cartDrawer, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.log('MutationObserver active on cart drawer');
  }

  /**
   * Main sync function: Fetch cart, evaluate discounts, update attributes
   */
  async syncDiscounts() {
    if (this.state.isProcessing) {
      this.log('⏳ Sync already in progress, queuing update');
      this.state.updateQueue.push(true);
      return;
    }

    this.state.isProcessing = true;

    try {
      // Poll for stable discount state (Discounty async handling)
      const cartData = await this.pollForStableCart();

      if (!cartData || !cartData.items) {
        this.log('❌ Failed to fetch cart data');
        this.state.isProcessing = false;
        return;
      }

      this.log('✅ Cart data fetched, evaluating discounts...');
      this.logCartData(cartData);

      // Track which lines gained or lost a discount, purely for logging.
      cartData.items.forEach((item) => this.evaluateItemDiscount(item));

      /**
       * Attributes are CART level. Compute them once from the whole cart and
       * write once - writing inside the item loop meant each item overwrote
       * the previous one's value.
       */
      await this.syncCartAttributes(cartData);
    } catch (error) {
      this.log('❌ Error in syncDiscounts:', error);
    } finally {
      this.state.isProcessing = false;

      // Process queued updates
      if (this.state.updateQueue.length > 0) {
        this.state.updateQueue = [];
        setTimeout(() => this.syncDiscounts(), 200);
      }
    }
  }

  /**
   * Poll /cart.js until discount values stabilize (for Discounty async)
   */
  async pollForStableCart() {
    const startTime = Date.now();
    let previousDiscounts = null;
    let stableReadCount = 0;

    while (Date.now() - startTime < this.config.pollTimeout) {
      const cartData = await this.fetchCart();

      if (!cartData || !cartData.items) {
        await this.sleep(this.config.pollInterval);
        continue;
      }

      // Extract total_discount from each item
      const currentDiscounts = cartData.items.map((item) => ({
        key: item.key,
        total_discount: item.total_discount,
      }));

      // Compare with previous read
      if (this.areDiscountsStable(previousDiscounts, currentDiscounts)) {
        stableReadCount++;
        this.log(
          `📊 Stable state detected (${stableReadCount}/${this.config.stabilityThreshold})`
        );

        if (stableReadCount >= this.config.stabilityThreshold) {
          this.log('🎯 Discount values stabilized');
          return cartData;
        }
      } else {
        stableReadCount = 0;
        this.log('⏳ Discounts still changing, waiting...');
      }

      previousDiscounts = currentDiscounts;
      await this.sleep(this.config.pollInterval);
    }

    this.log('⚠️  Timeout waiting for stable state, using last known state');
    return await this.fetchCart();
  }

  /**
   * Compare discount values to detect stability
   */
  areDiscountsStable(previous, current) {
    if (!previous) return false;
    if (!current) return false;
    if (previous.length !== current.length) return false;

    return previous.every((prev, idx) => {
      const curr = current[idx];
      return prev.key === curr.key && prev.total_discount === curr.total_discount;
    });
  }

  /**
   * Fetch cart data with simple caching
   */
  async fetchCart() {
    const now = Date.now();

    // Use cache if available
    if (
      this.fetchCache.lastFetch &&
      now - this.fetchCache.lastFetch < this.fetchCache.cacheDuration
    ) {
      return this.fetchCache.lastFetchData;
    }

    try {
      const response = await fetch('/cart.js');
      const data = await response.json();

      // Update cache
      this.fetchCache.lastFetch = now;
      this.fetchCache.lastFetchData = data;

      return data;
    } catch (error) {
      this.log('❌ Fetch error:', error);
      return null;
    }
  }

  /**
   * Note whether a line gained or lost its discount. Logging and state only -
   * this no longer produces a cart write.
   */
  evaluateItemDiscount(item) {
    const itemKey = item.key;
    const hasDiscount = item.total_discount > 0;
    const previousState = this.state.lastKnownDiscounts[itemKey];

    if (previousState === hasDiscount) return null;

    if (hasDiscount) {
      this.log(
        `💰 Discount applied to item ${itemKey}: $${(item.total_discount / 100).toFixed(2)}`
      );
      this.state.lastKnownDiscounts[itemKey] = true;
      return { itemKey, hasDiscount: true };
    }

    if (previousState === true) {
      this.log(`❌ Discount removed from item ${itemKey}`);
      this.state.lastKnownDiscounts[itemKey] = false;
      return { itemKey, hasDiscount: false };
    }

    return null;
  }

  /**
   * Write the cart-level discount attributes.
   *
   * IMPORTANT - this must never send a quantity.
   *
   * /cart/update.js accepts attributes on their own. The previous version also
   * sent "updates[<key>] = quantity" from a snapshot that could be seconds
   * old, which pushed the customer's quantity backwards whenever they clicked
   * + while a sync was running. Nothing in this function needs a quantity, so
   * nothing here sends one.
   */
  async syncCartAttributes(cartData) {
    const totalDiscount = cartData.items.reduce(
      (sum, item) => sum + (Number(item.total_discount) || 0),
      0
    );

    const hasAnyDiscount = totalDiscount > 0;

    const attributes = {
      _discount: hasAnyDiscount ? (totalDiscount / 100).toFixed(2) : '',
      _volume_discount: hasAnyDiscount ? 'true' : '',
    };

    // Skip the request when nothing has changed.
    const signature = JSON.stringify(attributes);
    if (signature === this.state.lastWrittenAttributes) {
      this.log('ℹ️  Attributes unchanged, skipping update');
      return null;
    }

    // Skip when the cart already holds these values.
    const live = cartData.attributes || {};
    const alreadyCorrect = Object.keys(attributes).every(
      (k) => String(live[k] || '') === String(attributes[k])
    );
    if (alreadyCorrect) {
      this.state.lastWrittenAttributes = signature;
      this.log('ℹ️  Attributes already correct on cart');
      return null;
    }

    Object.entries(attributes).forEach(([k, v]) => {
      this.log(`📝 Setting: ${k} = ${v === '' ? '(cleared)' : v}`);
    });

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        // attributes only - no updates[], so no quantity can be touched
        body: JSON.stringify({ attributes }),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed with status ${response.status}`);
      }

      const result = await response.json();
      this.state.lastWrittenAttributes = signature;
      this.log('✅ Cart attributes updated successfully');

      return result;
    } catch (error) {
      this.log('❌ Failed to update cart attributes:', error);
      throw error;
    }
  }

  /**
   * Utility: Sleep helper
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Utility: Formatted logging
   */
  log(...args) {
    if (this.config.debug) {
      console.log('[DiscountSync]', ...args);
    }
  }

  /**
   * Log cart data structure for debugging
   */
  logCartData(cartData) {
    if (!this.config.debug) return;

    cartData.items.forEach((item, idx) => {
      console.log(`  Item ${idx}:`, {
        key: item.key,
        title: item.title,
        quantity: item.quantity,
        total_discount: item.total_discount,
        properties: item.properties || {},
      });
    });
  }

  /**
   * Manual trigger for testing
   */
  async manualSync() {
    this.log('🔄 Manual sync triggered');
    await this.syncDiscounts();
  }

  /**
   * Get current discount state
   */
  getDiscountState() {
    return {
      lastKnownDiscounts: this.state.lastKnownDiscounts,
      isProcessing: this.state.isProcessing,
      config: this.config,
    };
  }

  /**
   * Update configuration at runtime
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.log('⚙️  Config updated:', this.config);
  }
}

// Export for use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DiscountSync;
}

// Auto-initialize if DOM ready
if (document.readyState !== 'loading') {
  window.discountSync = new DiscountSync({ debug: true });
} else {
  document.addEventListener('DOMContentLoaded', () => {
    window.discountSync = new DiscountSync({ debug: true });
  });
}